import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import Button from '@mui/material/Button';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { LoginDenialScreen } from '#components/login-denial-screen/login-denial-screen.js';
import { LoginDenialTone } from '#components/login-denial-screen/login-denial-tone.js';
import { Toast } from '#components/toast/toast.js';
import { useTRPC } from '#src/trpc.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { readPendingVerificationSubject } from './pending-email-verification.js';

export const Route = createFileRoute('/_authorization-gate/verify-email/')({
	head: () => ({ meta: [{ title: 'Verify your email • Cadstrom' }] }),
	component: VerifyEmailPage,
});

/**
 * Landing screen for a login denied because the user's email isn't verified yet.
 * Reached when the Auth0 Post-Login gate denies with the `email_unverified` code
 * (see auth0-callback-page.tsx). Prompts them to click the link in their inbox
 * and log in again — a fresh login re-runs the gate, which then passes.
 *
 * Offers a re-send when the denial named the account it turned away.
 */
export function VerifyEmailPage() {
	const el = useDataElTagGenerator(VerifyEmailPage);
	const addToast = useAddToast();
	const trpc = useTRPC();

	const [pendingSubject] = useState(readPendingVerificationSubject);

	const { mutateAsync: resendVerificationEmail, isPending: isResending } = useMutation(
		trpc.emailVerification.resend.mutationOptions(),
	);

	const handleResend = useCallback(() => {
		if (pendingSubject === null) {
			return;
		}

		void resendVerificationEmail({ auth0UserId: pendingSubject }).then(
			() => {
				addToast(
					<Toast severity='success' title='Verification email sent'>
						Open the new link to confirm your address.
					</Toast>,
				);
			},
			(error: unknown) => {
				addToast(
					<Toast severity='error' title='Couldn’t send the email'>
						{error instanceof Error ? error.message : 'Try again in a moment.'}
					</Toast>,
				);
			},
		);
	}, [addToast, pendingSubject, resendVerificationEmail]);

	return (
		<LoginDenialScreen
			icon={<MarkEmailUnreadOutlinedIcon className='text-[1.375rem]' />}
			tone={LoginDenialTone.Info}
			title='Verify your email'
			message='We’ve sent a verification link to your inbox. Confirm your address, then log in to continue.'
			loginLabel='Log in'
		>
			{pendingSubject !== null && (
				<Button {...el('resend')} variant='outlined' loading={isResending} onClick={handleResend} className='w-full'>
					<span>Resend verification email</span>
				</Button>
			)}
			<p className='text-gray-11'>Didn’t get it? Check your spam folder.</p>
		</LoginDenialScreen>
	);
}
